import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function SouthwarkCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Southwark Pothole Claims | A-Road Green Condition Halved | Fixtyer"
        description="Southwark Council's own data shows A-road green condition fell from 80% to 40% in four years, with 8,571 potholes filled in five years despite GREEN spend ratings. See what the DfT AMBER rating means for your claim."
        canonical="/councils/southwark"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Southwark', url: '/councils/southwark' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Southwark spends nearly nine times its DfT capital allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect and whether Southwark had reasonable notice of it under Section 58. The council\'s own condition data shows A-road green ratings halved from 80% to 40% between 2021 and 2024, and it admits TfL funding cuts accelerated deterioration — aggregate spend does not excuse an unrepaired pothole on your route.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 251.1km — roughly 74% of Southwark\'s 341km carriageway network. The council\'s own 2024 survey shows 4.8% of U-roads in RED condition and 45% in Amber, with green condition down from 81% in 2020 to 50.2% in 2024. Southwark claims annual inspections, but the published deterioration on residential streets is substantial.',
          },
          {
            question: 'Does Southwark\'s admission about TfL funding cuts help my claim?',
            answer: 'It can. Southwark states that "A Road condition has deteriorated since the COVID-19 lockdown forced Transport for London to reduce its financial support to London boroughs." That is documented knowledge that main-road deterioration was linked to reduced funding — which supports arguments that inspection and repair regimes may not have kept pace with known decline on affected routes.',
          },
          {
            question: 'Southwark says U-road RED condition is improving — does that weaken my claim?',
            answer: 'The RED percentage on U-roads is relatively low (4.8% in 2024), but Amber U-roads have tripled from 15% to 45% in the same period, and green U-roads have fallen from 81% to 50.2%. A road in Amber is, by the council\'s own definition, one where "maintenance may be required soon" — defects can form before resurfacing arrives.',
          },
          {
            question: 'Southwark filled over 8,500 potholes in five years — does that mean the roads are fixed?',
            answer: 'No. Southwark filled an estimated 8,571 potholes across 2021/22 to 2024/25 — peaking at 2,116 in 2024/25 — and projects 2,450 more in 2025/26. A network producing thousands of potholes annually is one where defects routinely form between inspections. Prior reports and photographic evidence of the specific defect remain decisive.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Southwark Council"
        councilSlug="southwark"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/southwark"
        headline="Southwark Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Southwark</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Southwark: A-Road Green Condition Halved in Four Years
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Southwark earns a GREEN spend scorecard and projects £5.35m capital investment in 2025/26 —
            nearly nine times its £614k DfT allocation. Yet the overall DfT rating is{' '}
            <strong className="text-amber-400">AMBER</strong> because the council's own surveys show
            A-road green condition collapsed from <strong className="text-emerald-400">80% in 2021</strong> to{' '}
            <strong className="text-rose-400">40% in 2024</strong>, with 60% of A-roads now Amber or Red —
            and the council blames TfL funding cuts for the decline.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">60%</div>
            <div className="text-white font-medium mt-2">Of A-roads now Amber or Red</div>
            <div className="text-brand-300 text-sm mt-1">
              45% Amber plus 15% Red in 2024 — on just 34.4km of A-roads including routes
              to Tower Bridge, Old Kent Road and Rotherhithe New Road.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of annual survey data from Southwark's own transparency report —
            main roads deteriorating, residential streets losing green condition
          </p>

          {/* A-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">A-roads (34.4km — 10% of network): deteriorating sharply</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">11%</td>
                    <td className="px-4 py-2 text-center">15%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">16%</td>
                    <td className="px-4 py-2 text-center text-emerald-600 font-medium">80%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">16.40%</td>
                    <td className="px-4 py-2 text-center">76.70%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center text-rose-600">14%</td>
                    <td className="px-4 py-2 text-center text-amber-600">39%</td>
                    <td className="px-4 py-2 text-center">47%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">15%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">45%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">40%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              Green A-roads halved in four years. Amber tripled from 15% to 45%. The council's
              own assessment: deterioration over the last two years on routes including Lordship Lane
              and Rotherhithe New Road — both on the 2025/26 resurfacing programme.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (55.41km — 16% of network): amber rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">9%</td>
                    <td className="px-4 py-2 text-center">15%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">70%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">28%</td>
                    <td className="px-4 py-2 text-center">64.50%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">11%</td>
                    <td className="px-4 py-2 text-center">38%</td>
                    <td className="px-4 py-2 text-center">51%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">11%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">44%</td>
                    <td className="px-4 py-2 text-center font-bold">44%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Green B/C roads down from 76% to 44%. The council notes RED condition is "improving"
              overall but admits "a minor deterioration in the last 2 years" — and 55% of B/C roads
              are now Amber or Red.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£614k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£5.35m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">90%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Southwark spends nearly <span className="text-emerald-400">nine times its DfT allocation</span> on
              capital highways work — plus £2.45m revenue — and A-road condition still collapsed. The
              council's own test: "If conditions are deteriorating, then investment levels are insufficient."
            </p>
          </div>
        </div>
      </section>

      {/* The U-road majority */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 251km Residential Majority
          </h2>
          <p className="text-surface-600 mb-8">
            74% of Southwark's carriageway network is unclassified roads — Camberwell, Peckham, Dulwich estate streets
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">Red</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-amber-700">Amber</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-emerald-700">Green</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center">4%</td>
                  <td className="px-6 py-4 text-center">15%</td>
                  <td className="px-6 py-4 text-center text-emerald-600 font-medium">81%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center">3%</td>
                  <td className="px-6 py-4 text-center">15%</td>
                  <td className="px-6 py-4 text-center">81%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center">8.60%</td>
                  <td className="px-6 py-4 text-center">17.20%</td>
                  <td className="px-6 py-4 text-center">74.20%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center">5%</td>
                  <td className="px-6 py-4 text-center text-amber-600">41.20%</td>
                  <td className="px-6 py-4 text-center">53.80%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold">4.80%</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">45%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">50.20%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Amber Surge</h3>
              <p className="text-surface-600 text-sm mb-3">
                Southwark states "The trend of U roads in Red condition in Southwark is improving" —
                RED sits at 4.8%. But Amber U-roads have tripled from 15% to 45%, and green U-roads
                have fallen from 81% to 50.2%. By the council's own categories, nearly half of
                residential streets now need maintenance "soon."
              </p>
              <p className="text-surface-600 text-sm">
                That is roughly <strong>113km of U-roads in Amber condition</strong> — plus 12km in RED —
                on a network the council also maintains alongside 780km of footways.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Annual Inspections — With Limits</h3>
              <p className="text-amber-800 text-sm mb-3">
                Southwark claims "detailed inspections of our roads and footways every year" and loads
                results into asset-management software that "simulates how roads might deteriorate."
              </p>
              <p className="text-amber-800 text-sm">
                Simulation is not the same as catching every defect. With 2,000+ potholes filled annually
                and green U-road condition down 31 points since 2020, the gap between survey data and
                street-level safety is where claims are won or lost.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Southwark must show a reasonable system for knowing
              road condition. For the borough's 251km of U-roads, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your street in the 45% of U-roads surveyed as Amber in 2024?</li>
              <li>• If maintenance "may be required soon," why was the specific defect not treated before your incident?</li>
              <li>• Did annual inspection data for your road translate into a scheduled repair?</li>
              <li>• With 780km of footways also maintained, how does the council prioritise reactive pothole response?</li>
            </ul>
            <p className="text-white font-medium">
              A council that knows half its residential network is Amber or Red cannot claim
              surprise when potholes form on those streets.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            8,571 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated pothole fills from Southwark's own spending transparency data
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimate)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Cost</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">% of reactive budget</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,119</td>
                  <td className="px-6 py-4 text-center">£30,034</td>
                  <td className="px-6 py-4 text-center">1.20%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23*</td>
                  <td className="px-6 py-4 text-center">1,815</td>
                  <td className="px-6 py-4 text-center">£51,039</td>
                  <td className="px-6 py-4 text-center">2.80%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,484</td>
                  <td className="px-6 py-4 text-center">£50,619</td>
                  <td className="px-6 py-4 text-center">2.00%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">2,037</td>
                  <td className="px-6 py-4 text-center">£66,900</td>
                  <td className="px-6 py-4 text-center">2.30%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,116</td>
                  <td className="px-6 py-4 text-center">£66,907</td>
                  <td className="px-6 py-4 text-center">1.70%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">8,571</td>
                  <td className="px-6 py-4 text-center font-bold">£265,499</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~5 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Southwark fills an estimated five potholes daily across its
                341km network — rising to nearly six per day in 2024/25. The council projects 2,450
                fills in 2025/26. Defects that form between reactive repairs are exactly where prior
                reports and dated photographs decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Reactive Spend vs Pothole Cost</h3>
              <p className="text-amber-800 text-sm mb-3">
                Pothole fills account for just 1.2–2.8% of Southwark's reactive budget each year —
                the bulk of the £2.45m annual revenue spend covers gritting, gullies, grass cutting
                and traffic signals. The council anticipates spending "two and a half times more on
                preventative works than reactive works" in 2025/26, with 71% preventative overall.
              </p>
              <p className="text-amber-800 text-xs">
                *The council's published pothole table labels one column "2022/22" — likely a typo for 2022/23.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TfL funding admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The TfL Funding Cut Admission
          </h2>
          <p className="text-surface-600 mb-8">
            Southwark's own explanation for A-road deterioration — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="A Road condition has deteriorated since the COVID-19 lockdown forced Transport for London to reduce its financial support to London boroughs. Because of this, we have increased our capital investment in A Road resurfacing."
              source="Southwark Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The trend of A roads in Red condition is steady, but with a deterioration over the last 2 years. To ensure the A road Red condition returns to a steady or improving trend, we have invested more capital funds to the A road resurfacing programme."
              source="Southwark Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="If conditions are deteriorating, then investment levels are insufficient."
              source="Southwark Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Admission Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Southwark formally links A-road decline to reduced TfL borough funding after COVID-19.
                That is documented knowledge that main-road deterioration was foreseeable — and that
                the council's response (increased capital for A-road resurfacing) came after condition
                had already collapsed.
              </p>
              <p className="text-surface-600 text-sm">
                The 2025/26 programme includes Lordship Lane (£448,695 renewal) and Rotherhithe New Road
                (£371,389 structural surface) — routes already showing 15% RED and 45% Amber in 2024 surveys.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your incident on an A-road that was already Amber or Red in published surveys?</li>
                <li>• If TfL cuts were known to cause deterioration, was inspection frequency increased?</li>
                <li>• Did resurfacing reach your section before or after your damage?</li>
                <li>• Can the council prove the specific defect was not on a scheduled treatment list?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology and forward plans */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Plans and the PAS 2161 Switch
          </h2>
          <p className="text-surface-600 mb-8">
            How Southwark says it manages 341km of roads, 780km of footways and 54.5km of cycleways
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey Methodology</h3>
              <p className="text-surface-600 text-sm mb-3">
                Southwark completes "a detailed assessment of the conditions of our roads every year,"
                categorising each section as Green (no treatment required), Amber (maintenance may be
                required soon) or Red (should be considered for maintenance).
              </p>
              <p className="text-surface-600 text-sm">
                From 2026/27 a new PAS 2161 methodology will replace the three-category system with five
                categories. Southwark states it has "already adopted the new methodology" — meaning
                pre- and post-switch condition records may not be directly comparable.
              </p>
            </div>

            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 Forward Programme</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Resurface approximately 1.7 miles of carriageway</li>
                <li>• Improve 70 footway sections in 42 streets</li>
                <li>• Repair Rope Street, Lavender Lock and South Street bridges</li>
                <li>• Projected capital spend: £5.35m; revenue: £2.45m</li>
                <li>• Estimated 2,450 pothole fills</li>
              </ul>
            </div>
          </div>

          <QuoteCard
            quote="We also estimate estimate we will fill 2,450 potholes, based on a projection of the last 5 years' figures."
            source="Southwark Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Southwark invests heavily — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital investment far exceeds DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 90% preventative share projected for 2025/26; 71% overall preventative spend planned
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Claims annual road and footway inspections with asset-management software
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> NHT survey: residents rate Southwark highway performance best in the country
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED condition relatively low at 4.8%
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence citing inspection regimes and risk-based asset management.
                Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — A-road green halved from 80% to 40% in four years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — not the top tier despite resident survey claims
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 45% of U-roads and 45% of A-roads in Amber — maintenance "required soon"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 8,571 potholes filled in five years — defects form faster than reactive repairs
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits TfL funding cuts caused A-road deterioration
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council's own test: deteriorating conditions mean insufficient investment
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and documented inspection software, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class and 2024 survey colour — Amber roads are flagged for imminent maintenance</li>
              <li>• Whether your route was on the published 2025/26 resurfacing programme before your incident</li>
              <li>• Location on A-road corridors where the council admits post-COVID deterioration</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Southwark's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Southwark?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No A-road deterioration data cited</li>
                <li>• No prior-report search</li>
                <li>• No TfL funding-cut argument</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ A-road green collapse documented (80% → 40%)</li>
                <li>✅ U-road amber surge argued (15% → 45%)</li>
                <li>✅ 8,571 pothole fills in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Southwark</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=southwark"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Southwark Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Southwark spends nearly nine times its DfT capital allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect and whether
                Southwark had reasonable notice of it under Section 58. The council's own condition data shows
                A-road green ratings halved from 80% to 40% between 2021 and 2024, and it admits TfL funding
                cuts accelerated deterioration — aggregate spend does not excuse an unrepaired pothole on your route.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 251.1km — roughly 74% of Southwark's 341km carriageway network. The council's
                own 2024 survey shows 4.8% of U-roads in RED condition and 45% in Amber, with green condition
                down from 81% in 2020 to 50.2% in 2024. Southwark claims annual inspections, but the published
                deterioration on residential streets is substantial.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Southwark's admission about TfL funding cuts help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Southwark states that "A Road condition has deteriorated since the COVID-19 lockdown
                forced Transport for London to reduce its financial support to London boroughs." That is documented
                knowledge that main-road deterioration was linked to reduced funding — which supports arguments
                that inspection and repair regimes may not have kept pace with known decline on affected routes.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Southwark says U-road RED condition is improving — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The RED percentage on U-roads is relatively low (4.8% in 2024), but Amber U-roads have tripled
                from 15% to 45% in the same period, and green U-roads have fallen from 81% to 50.2%. A road in
                Amber is, by the council's own definition, one where "maintenance may be required soon" — defects
                can form before resurfacing arrives.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Southwark filled over 8,500 potholes in five years — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Southwark filled an estimated 8,571 potholes across 2021/22 to 2024/25 — peaking at 2,116
                in 2024/25 — and projects 2,450 more in 2025/26. A network producing thousands of potholes annually
                is one where defects routinely form between inspections. Prior reports and photographic evidence
                of the specific defect remain decisive.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.southwark.gov.uk/parking-streets-and-transport/our-local-highways-maintenance-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Southwark Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
